import { useForecast }     from '../hooks/useForecast';
import { useForecastGrid } from '../hooks/useForecastGrid';
import LoadingSpinner      from '../components/LoadingSpinner';
import ErrorCard           from '../components/ErrorCard';
import SummaryCard         from '../components/SummaryCard';
import ForecastGrid        from '../components/ForecastGrid';

function fmt(n) {
  if (!n && n !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style:                 'currency',
    currency:              'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function Forecast() {
  const { data, loading, error, refresh, lastSync } = useForecast();
  const { data: gridData, loading: gridLoading } = useForecastGrid();

  if (loading) return <div className="page"><LoadingSpinner message="Loading forecast data..." /></div>;
  if (error)   return <div className="page"><ErrorCard error={error} onRetry={refresh} /></div>;
  if (!data)   return null;

  const { rateOfClimb, forecast, annualBudget, actuals, variance, period } = data;
  const rocColor = rateOfClimb.climbing ? '#15803D' : '#BE123C';
  const rocBg    = rateOfClimb.climbing ? '#DCFCE7' : '#FFE4E6';
  const syncStr  = lastSync ? `Last sync: ${lastSync.toLocaleTimeString()}` : '';

  return (
    <div className="page">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:'1.5rem' }}>
        <h2>Forecast — {period.year}</h2>
        <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
          <span style={{ color:'#94A3B8', fontSize:'0.8rem' }}>{syncStr}</span>
          <button onClick={refresh}
            style={{ padding:'0.4rem 1rem', background:'#0E7C7B', color:'#fff',
                     border:'none', borderRadius:4, cursor:'pointer' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Rate of Climb banner */}
      <div style={{
        background: rocBg, border: `2px solid ${rocColor}`,
        borderRadius: 8, padding: '1.5rem 2rem',
        marginBottom: '1.5rem', textAlign: 'center',
      }}>
        <div style={{ fontSize:'2.5rem', fontWeight:800, color:rocColor,
                      letterSpacing:'0.05em' }}>
          {rateOfClimb.arrow} {rateOfClimb.verdict}
        </div>
        <div style={{ color:rocColor, fontSize:'1rem', marginTop:'0.5rem', fontWeight:500 }}>
          {rateOfClimb.label}
        </div>
        <div style={{ color:'#64748B', fontSize:'0.85rem', marginTop:'0.5rem' }}>
          {period.closedMonths} months actual · {period.remainingMonths} months forecast remaining
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)',
                    gap:'1rem', marginBottom:'2rem' }}>
                      
        <SummaryCard
          icon="📊" title="YTD Actual Net"
          value={fmt(actuals.net)}
          sub={`Through ${period.closedMonths} months`}
          color="#0369A1"
        />
        <SummaryCard
          icon={rateOfClimb.arrow} title="Year Forecast Net"
          value={fmt(forecast.net)}
          sub={`vs budget: ${fmt(annualBudget.net)}`}
          color={rocColor}
        />
        <SummaryCard
          icon="🎯" title="Variance vs Budget"
          value={(variance.net >= 0 ? '▲ ' : '▼ ') + fmt(Math.abs(variance.net))}
          sub={`${Math.abs(variance.pct)}% ${variance.net >= 0 ? 'ahead of' : 'behind'} plan`}
          color={variance.net >= 0 ? '#15803D' : '#BE123C'}
        />
      </div>

      {/* Projection Forecast Grid */}
      <div style={{ background:'#fff', borderRadius:8, padding:'1.5rem',
                    boxShadow:'0 1px 3px rgba(0,0,0,0.08)', marginTop:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', marginBottom:'1rem' }}>
          <h3 style={{ color:'#1B2A4A', fontSize:'1rem', margin:0 }}>
            Projection Forecast — {period.year}
          </h3>
          <div style={{ fontSize:'0.8rem', color:'#64748B' }}>
            Read-only · Edit in{' '}
            <a href="/sandbox"
              style={{ color:'#0E7C7B', textDecoration:'none', fontWeight:600 }}>
              Sandbox ↗
            </a>
          </div>
        </div>
        {gridLoading && (
          <p style={{ color:'#94A3B8' }}>Loading projection data...</p>
        )}
        {!gridLoading && <ForecastGrid data={gridData} />}
      </div>

      {/* Income vs Expenses breakdown */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr',
                    gap:'1rem', marginTop:'1rem' }}>
        {[
          { label:'Income',   actual:actuals.income,
            forecast:forecast.income,   budget:annualBudget.income },
          { label:'Expenses', actual:actuals.expenses,
            forecast:forecast.expenses, budget:annualBudget.expenses },
        ].map(({ label, actual, forecast: fc, budget }) => (
          <div key={label} style={{ background:'#fff', borderRadius:8,
                                     padding:'1.25rem',
                                     boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ fontWeight:700, color:'#1B2A4A', marginBottom:'0.75rem' }}>
              {label}
            </div>
            <div style={{ display:'flex', justifyContent:'space-between',
                           fontSize:'0.85rem', marginBottom:'0.4rem' }}>
              <span style={{ color:'#64748B' }}>YTD Actual</span>
              <span style={{ fontWeight:600 }}>{fmt(actual)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between',
                           fontSize:'0.85rem', marginBottom:'0.4rem' }}>
              <span style={{ color:'#64748B' }}>Year Forecast</span>
              <span style={{ fontWeight:600 }}>{fmt(fc)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between',
                           fontSize:'0.85rem' }}>
              <span style={{ color:'#64748B' }}>Annual Budget</span>
              <span style={{ color:'#94A3B8' }}>{fmt(budget)}</span>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

export default Forecast;